const PlummeCustomChartAccounts = (() => {
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const codeText = (value) => String(value ?? '').trim();
  const cleanCode = (value) => codeText(value).replace(/\D/g, '').slice(0, 4);
  const isAccountMatch = (value, code) => codeText(value) === code;
  const accountCode = (account) => cleanCode(account?.code || account?.accountNumber || account?.id);

  const countObjectFields = (source, code, fields) =>
    fields.reduce((count, field) => count + (isAccountMatch(source?.[field], code) ? 1 : 0), 0);

  const addUsage = (usages, label, count) => {
    if (count > 0) usages.push({ label, count });
  };

  const countRows = (rows, code, fields) =>
    safeArray(rows).reduce((sum, row) => sum + countObjectFields(row, code, fields), 0);

  const countNestedRows = (rows, code, nestedFields, fields) =>
    safeArray(rows).reduce((sum, row) => sum + nestedFields.reduce((nestedSum, nestedField) =>
      nestedSum + countRows(row?.[nestedField], code, fields), 0), 0);

  const customAccountUsage = (state = {}, accountOrCode) => {
    const code = cleanCode(typeof accountOrCode === 'string' ? accountOrCode : accountCode(accountOrCode));
    const usages = [];
    if (!code) return { code, total: 0, usages, labels: [] };

    addUsage(usages, 'bankoppsett', countRows(state.settings?.bankAccounts, code, ['ledger', 'ledgerAccountCode', 'accountCode', 'code']) +
      countRows(state.settings?.paymentAccounts, code, ['ledger', 'ledgerAccountCode', 'accountCode', 'code']) +
      countObjectFields(state.settings || {}, code, ['bankAccountingAccount']));

    Object.values(state.openingBalanceByYear || {}).forEach((opening) => {
      addUsage(usages, 'inngående balanse', countRows(opening?.balanceLines, code, ['accountCode', 'code']) +
        countRows(opening?.salesByAccount, code, ['accountCode', 'code']) +
        countRows(opening?.expensesByAccount, code, ['accountCode', 'code']));
    });

    addUsage(usages, 'fakturaer', countRows(state.invoices, code, ['salesAccountCode', 'settlementAccountCode', 'accountCode']) +
      countNestedRows(state.invoices, code, ['lines', 'payments'], ['accountCode', 'salesAccountCode', 'settlementAccountCode']));
    addUsage(usages, 'andre salg', countRows(state.otherSales, code, ['salesAccountCode', 'settlementAccountCode', 'accountCode']) +
      countNestedRows(state.otherSales, code, ['lines', 'payments'], ['accountCode', 'salesAccountCode', 'settlementAccountCode']));
    addUsage(usages, 'kjøp og fri postering', countRows(state.expenses, code, ['accountCode', 'costAccountCode', 'expenseAccountCode', 'categoryAccountCode', 'settlementAccountCode']) +
      countNestedRows(state.expenses, code, ['purchaseLines', 'lines', 'payments', 'manualPostingLines'], ['accountCode', 'costAccountCode', 'settlementAccountCode', 'fromAccount', 'toAccount']));
    addUsage(usages, 'bankavstemming', countRows(state.bankReconciliationPostings, code, ['accountCode', 'fromAccountCode', 'toAccountCode', 'settlementAccountCode']) +
      countNestedRows(state.bankStatements, code, ['lines', 'entries', 'transactions'], ['accountCode', 'aiSuggestedAccountCode', 'fromAccountCode', 'toAccountCode', 'settlementAccountCode']));
    addUsage(usages, 'budsjett', countRows(state.budgetLines, code, ['accountCode']) +
      countNestedRows(Object.values(state.budgetsByYear || {}), code, ['lines'], ['accountCode']));
    addUsage(usages, 'lønn', countRows(state.payrollRuns, code, ['accountCode', 'settlementAccountCode']) +
      countNestedRows(state.payrollRuns, code, ['lines', 'postings', 'payments'], ['accountCode', 'settlementAccountCode']) +
      countRows(state.settings?.customPayTypes, code, ['accountFromCode', 'accountCode']));
    addUsage(usages, 'importert hovedbok', countRows(state.accountingImportLedgerRows, code, ['accountCode', 'account']) +
      countNestedRows(Object.values(state.accountingImportReportStatsByYear || {}), code, ['accounts', 'monthly'], ['accountCode']));
    addUsage(usages, 'tolldeklarasjoner', countRows(state.customsDeclarations, code, ['accountCode', 'settlementAccountCode']) +
      countNestedRows(state.customsDeclarations, code, ['lines', 'postings'], ['accountCode', 'settlementAccountCode']));

    const total = usages.reduce((sum, entry) => sum + entry.count, 0);
    return { code, total, usages, labels: usages.map((entry) => entry.label) };
  };

  const normalizeCustomAccount = (draft = {}) => {
    const code = accountCode(draft);
    const equivalentCode = cleanCode(draft.equivalentCode || draft.yearClosingStandardAccountCode || draft.standardAccountCode || draft.mapsToAccountCode);
    return {
      ...draft,
      id: draft.id || code,
      code,
      name: codeText(draft.name) || `Konto ${code}`,
      custom: true,
      active: draft.active !== false,
      equivalentCode,
      yearClosingStandardAccountCode: equivalentCode,
    };
  };

  return { accountCode, customAccountUsage, normalizeCustomAccount };
})();

globalThis.PlummeCustomChartAccounts = PlummeCustomChartAccounts;
